import axios from 'axios';
import { authService } from '@/auth/services/AuthService';
import { emitAuthFailureFromApiError } from '@/auth/authFailureEvents';
import { toApiError } from '@/api/errorHandler';
import { authConfig } from '@/config/authConfig';
import { urls } from '@/config/urls';

export const apiClient = axios.create({
  baseURL: urls.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

const resolveApiAccessToken = async (): Promise<string | null> => {
  return authService.acquireAccessToken(authConfig.apiScopes);
};

const isHealthRequest = (url: string | undefined): boolean => {
  if (!url) {
    return false;
  }

  return /(?:^|\/)health(?:\?|$)/.test(url);
};

apiClient.interceptors.request.use(async (config) => {
  if (isHealthRequest(config.url)) {
    return config;
  }

  const headerBag = config.headers;

  const existingAuthHeader =
    typeof headerBag?.get === 'function'
      ? headerBag.get('Authorization')
      : (headerBag?.Authorization ?? headerBag?.authorization);

  if (existingAuthHeader) {
    return config;
  }

  const token = await resolveApiAccessToken();

  if (!token) {
    return config;
  }

  if (typeof headerBag?.set === 'function') {
    headerBag.set('Authorization', `Bearer ${token}`);
    return config;
  }

  (config.headers as Record<string, string | undefined>).Authorization =
    `Bearer ${token}`;

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const apiError = toApiError(error);
    emitAuthFailureFromApiError(apiError);
    return Promise.reject(apiError);
  },
);
